import React, { useMemo } from "react";
import { Group, isGroup, Option } from "./MultiSelect";

const extractText = (node: React.ReactNode): string => {
  if (typeof node === 'string') return node;
  if (Array.isArray(node)) return node.map(extractText).join('');
  if (React.isValidElement(node)) return extractText(node.props.children);
  return '';
};

function useFlatOptionList(options: (Option | Group)[]) {
  return useMemo(() => {
    return options.reduce<Record<string, Option>>((acc, option) => {
      if (isGroup(option)) {
        option.options.forEach((o) => {
          acc[o.value] = o;
        });
      } else {
        acc[option.value] = option;
      }
      return acc;
    }, {});
  }, [options]);
}

export function useGroupOptions(options: (Option | Group)[]) {
  const flatOptions = useFlatOptionList(options);

  const getOption = (value: string) => flatOptions[value];

  const getGroupForOption = (value: string) => {
    const group = options.find(
      (option) => isGroup(option) && option.value === value,
    );
    if (!group) return;
    return group;
  };

  const filterOptions = (searchTerm: string) => {
    const term = searchTerm.toLowerCase();
    return options.reduce<(Option | Group)[]>((acc, option) => {
      if (isGroup(option)) {
        const filteredGroupOptions = option.options.filter((o) =>
          extractText(o.label).toLowerCase().includes(term),
        );
        if (filteredGroupOptions.length > 0) {
          acc.push({ ...option, options: filteredGroupOptions });
        }
      } else if (extractText(option.label).toLowerCase().includes(term)) {
        acc.push(option);
      }
      return acc;
    }, []);
  };

  return {
    getOption,
    getGroupForOption,
    filterOptions,
  };
}
